/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo, useState} from 'react';
import {Drawer, IconButton} from '@material-ui/core';
import MenuIcon from '@material-ui/icons/Menu';
import ResizableDraggableContent from '@ion/dls/src/components/resizable/ResizableDraggableContent';
import {ResizeGrabber} from '@ion/dls/src/components/resizable/ResizeGrabber';
import Hidden from '@ion/dls/src/layout/Hidden';
import {dlsColor, dlsZIndex} from '@ion/dls/src/theme/theme';
import {useThemeMode} from '@ion/dls/src/theme/useThemeContext';
import {MAX_DRAWER_WIDTH, MIN_DRAWER_WIDTH, PANEL_WIDTH} from './types';

/**
 * Props for DashboardDrawer
 */
export interface DashboardDrawerProps {
  mobileOpen: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  drawerClasses?: string;
  toggleButtonClass?: string;
}

const overlayStyle: any = {
  position        : 'fixed',
  top             : 0,
  left            : 0,
  right           : 0,
  bottom          : 0,
  zIndex          : 9999,
  backgroundColor : 'rgba(0, 0, 0, 0.05)',
  cursor          : 'col-resize'
};

const enableResizing = {
  right       : true,
  left        : false,
  top         : false,
  bottom      : false,
  topRight    : false,
  bottomRight : false,
  bottomLeft  : false,
  topLeft     : false
};

/**
 * DashboardDrawer renders a responsive sidebar drawer that adapts
 * its behavior based on screen size. On **mobile devices**, it
 * shows a temporary drawer with a toggle button to open/close
 * the panel. On **desktop**, it renders the drawer content inline
 * without toggling behavior.
 *
 * ```typescript
 * <DashboardDrawer mobileOpen={isDrawerOpen}
 *   onToggle={() => setDrawerOpen(!isDrawerOpen)}
 *   children={<SidebarMenu />}
 *   drawerClasses='custom-paper'
 *   toggleButtonClass='menu-toggle-btn' />
 * ```
 */
const DashboardDrawer = ({
  mobileOpen,
  onToggle,
  children,
  drawerClasses,
  toggleButtonClass
}: DashboardDrawerProps) => {
  const [drawerWidth, setDrawerWidth] = useState(PANEL_WIDTH);
  const [isResizing, setIsResizing]   = useState(false);
  const {mode}                        = useThemeMode();

  const onResizeStart = useCallback(function() { setIsResizing(true); }, []);

  const onResizeStop = useCallback(function(e, dir, ref) {
    const newWidth = parseInt(ref.style.width, 10);
    if (newWidth <= MIN_DRAWER_WIDTH) {
      setDrawerWidth(8);
    } else {
      setDrawerWidth(newWidth);
    }
    setIsResizing(false);
  }, []);

  const resizeHandleComponent = useMemo(() => ({
    right : (
      <ResizeGrabber style={{height: '100%', marginLeft: '1px'}}
        color={dlsColor('caBlue')}
        hasReport
        isVertical />
    )
  }), [mode]);

  return (
    <>
      <Hidden show='mobile'>
        <Drawer variant='temporary'
          open={mobileOpen}
          onClose={onToggle}
          classes={{paper: drawerClasses}}
          ModalProps={{keepMounted: true}}>
          {children}
        </Drawer>
      </Hidden>

      <Hidden show='desktop'>
        <div style={{position: 'relative'}}>
          {isResizing && (
            <div style={overlayStyle} />
          )}

          <ResizableDraggableContent defaultSize={{
            x      : 0,
            y      : 0,
            width  : drawerWidth,
            height : '100%'
          }}
          size={{
            width  : drawerWidth,
            height : '99%'
          }}
          maxHeight='99%'
          enableResizing={enableResizing}
          maxWidth={MAX_DRAWER_WIDTH}
          onResizeStart={onResizeStart}
          onResizeStop={onResizeStop}
          resizeHandleComponent={resizeHandleComponent}
          style={{
            overflow        : 'visible',
            backgroundColor : dlsColor('white'),
            boxShadow       : '0px 0px 6px rgba(0, 0, 0, 0.1)',
            position        : 'relative',
            zIndex          : dlsZIndex('resizableDashboardDrawerDraggableContent'),
            pointerEvents   : isResizing ? 'none' : 'auto'
          }}
          className={drawerClasses}>
            <div style={isResizing? {
              pointerEvents : 'none',
              height        : '100%'
            } : undefined}>
              {children}
            </div>
          </ResizableDraggableContent>
        </div>
      </Hidden>

      <Hidden show='mobile'>
        <IconButton className={toggleButtonClass} onClick={onToggle}>
          <MenuIcon />
        </IconButton>
      </Hidden>
    </>
  );
};

export default DashboardDrawer;
